import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { StreakManager, StreakData } from "../../lib/StreakManager";
import { useXP } from "../../hooks/use-xp";
import { XPRewardCalculator } from "../../lib/xp-rewards";
import { analytics } from "../../lib/analytics";
import styles from "./StreakWidget.module.scss";

export interface StreakWidgetProps {
  size?: "small" | "medium" | "large";
  showDetails?: boolean;
  autoCheckIn?: boolean;
  onStreakUpdate?: (streak: StreakData) => void;
  className?: string;
}

export const StreakWidget: React.FC<StreakWidgetProps> = ({
  size = "medium",
  showDetails = true,
  autoCheckIn = true,
  onStreakUpdate,
  className,
}) => {
  const [streak, setStreak] = useState<StreakData>({
    current: 0,
    longest: 0,
    lastActiveDate: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const manager = useRef(new StreakManager());
  const { addXP } = useXP();
  const xpCalculator = useMemo(() => new XPRewardCalculator(), []);

  // Load streak data and auto check-in
  const loadStreakData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = manager.current.getStreakData();
      setStreak(data);

      if (autoCheckIn) {
        const update = manager.current.checkIn();

        if (update.isNew && update.current > 0) {
          // Award XP for maintaining streak
          const streakReward = xpCalculator.calculateStreakXP(update.current);
          addXP(streakReward.amount, streakReward.reason);

          // Track streak maintenance with PostHog
          analytics.track("streak_maintained", {
            streak_type: "daily_tasks",
            current_streak: update.current,
            longest_streak: Math.max(update.current, streak.longest),
            streak_bonus_xp: streakReward.amount,
          });

          // Show celebration for milestones
          if (update.milestone) {
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), 3000);
          }

          // Update display with new streak data
          const updatedData = manager.current.getStreakData();
          setStreak(updatedData);
          onStreakUpdate?.(updatedData);

          console.log(`Streak check-in: Day ${update.current}! Earned ${streakReward.amount} XP`);
        }
      }
    } catch (error) {
      console.error("Error loading streak data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [autoCheckIn, addXP, onStreakUpdate, xpCalculator, streak.longest]);

  // Manual check-in function
  const handleCheckIn = useCallback(() => {
    const update = manager.current.checkIn();
    const updatedData = manager.current.getStreakData();
    setStreak(updatedData);
    onStreakUpdate?.(updatedData);

    if (update.isNew) {
      const streakReward = xpCalculator.calculateStreakXP(update.current);
      addXP(streakReward.amount, streakReward.reason);

      if (update.milestone) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      }
    }
  }, [addXP, onStreakUpdate, xpCalculator]);

  // Listen for streak updates from other components
  useEffect(() => {
    const handleStreakUpdate = (event: CustomEvent) => {
      setStreak(event.detail);
      onStreakUpdate?.(event.detail);
    };

    window.addEventListener("streakUpdate", handleStreakUpdate as EventListener);

    return () => {
      window.removeEventListener("streakUpdate", handleStreakUpdate as EventListener);
    };
  }, [onStreakUpdate]);

  // Initial load
  useEffect(() => {
    loadStreakData();
  }, [loadStreakData]);

  // Get flame emoji based on streak length
  const getFlameEmoji = () => {
    if (streak.current === 0) return "🌱"; // Seed for new start
    if (streak.current >= 100) return "🏆"; // Trophy for 100+ days
    if (streak.current >= 30) return "⭐"; // Star for 30+ days
    if (streak.current >= 7) return "🔥"; // Fire for 7+ days
    return "💫"; // Sparkle for 1-6 days
  };

  // Get streak status
  const getStreakStatus = () => {
    const status = manager.current.getStreakStatus();
    return status;
  };

  // Get next milestone info
  const getNextMilestone = () => {
    const milestone = manager.current.getNextMilestone();
    return milestone;
  };

  const status = getStreakStatus();
  const nextMilestone = getNextMilestone();

  if (isLoading) {
    return (
      <div
        className={`${styles.streakWidget} ${styles[size]} ${styles.loading} ${className || ""}`}
      >
        <div className={styles.spinner} data-testid="streak-loading">
          ⏳
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.streakWidget} ${styles[size]} ${showCelebration ? styles.celebrating : ""} ${className || ""}`}
      data-testid="streak-widget"
    >
      <div className={styles.main}>
        <div className={styles.flame} data-testid="streak-flame">
          {getFlameEmoji()}
        </div>

        <div className={styles.streakNumber} data-testid="streak-number">
          {streak.current}
        </div>

        <div className={styles.label}>{streak.current === 1 ? "Day Streak" : "Day Streak"}</div>
      </div>

      {showDetails && (
        <div className={styles.details} data-testid="streak-details">
          <div className={styles.status} data-testid="streak-status">
            <span className={`${styles.statusIcon} ${styles[status.status]}`}>
              {status.status === "active" ? "✅" : status.status === "broken" ? "💔" : "🌟"}
            </span>
            <span className={styles.statusMessage}>{status.message}</span>
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Best:</span>
              <span className={styles.statValue} data-testid="streak-best">
                {streak.longest}
              </span>
            </div>

            {nextMilestone && (
              <div className={styles.milestone}>
                <span className={styles.milestoneLabel}>Next Goal:</span>
                <span className={styles.milestoneValue} data-testid="streak-milestone">
                  {nextMilestone.days} days
                </span>
              </div>
            )}
          </div>

          {status.status !== "active" && (
            <button
              onClick={handleCheckIn}
              className={styles.checkInButton}
              data-testid="streak-checkin-button"
            >
              Check In Today
            </button>
          )}
        </div>
      )}

      {/* Celebration animation overlay */}
      {showCelebration && (
        <div className={styles.celebration} data-testid="streak-celebration">
          <div className={styles.celebrationText}>Milestone Reached! 🎉</div>
          <div className={styles.confetti}>
            <span>🎊</span>
            <span>🎉</span>
            <span>✨</span>
            <span>🎊</span>
            <span>🎉</span>
          </div>
        </div>
      )}

      {/* Tooltip with detailed stats */}
      <div className={styles.tooltip} data-testid="streak-tooltip">
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipRow}>
            <strong>Current Streak:</strong> {streak.current} days
          </div>
          <div className={styles.tooltipRow}>
            <strong>Longest Streak:</strong> {streak.longest} days
          </div>
          <div className={styles.tooltipRow}>
            <strong>Milestones:</strong> {streak.milestones?.length || 0}
          </div>
          {streak.lastActiveDate && (
            <div className={styles.tooltipRow}>
              <strong>Last Active:</strong> {new Date(streak.lastActiveDate).toLocaleDateString()}
            </div>
          )}
          {nextMilestone && (
            <div className={styles.tooltipRow}>
              <strong>Next Goal:</strong> {nextMilestone.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
